import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  SvgIcon,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MediaInfo } from "@components/client/elements/media-info";
import { Bookmark } from "@components/client/elements/bookmark";
import { AssetBreadcrumbs } from "../asset-breadcrumbs";
import useAssetStore from "@stores/client/useAssetStore";
import { createMediaInfo } from "@utils/client/create-media-info";
import { getIconByName } from "@utils/client/get-icon-by-name";
import { getNodeIcon } from "@utils/shared/get-node-icon";
import { useTranslation } from "react-i18next";

// Bookmark floats in the top-right corner, clear of the title/breadcrumb.
// Scaled down a touch so it reads as an accent, not a dominant control.
const IconBox = styled("div")({
  position: "absolute",
  right: 2,
  top: -18,
  zIndex: 1000,
  transform: "scale(0.95)",
  transformOrigin: "top right",
});

export const AssetHoverCard = ({ item, hasBreadcrumb = false }) => {
  const { currentRootId } = useAssetStore();
  const { t } = useTranslation();
  const theme = useTheme();
  // Transactions no longer have their own page (they're read inside their
  // process), so only processes can be bookmarked — same rule as list/grid.
  const canBookmark = item.type === 2;
  // Resolve the icon exactly like the tree rows: the item's custom icon when it
  // specifies one, otherwise the type-based default (area/process/transaction),
  // so the hover card and the tree read as the same asset.
  const assetIcon = getIconByName(item.icon) ?? getNodeIcon(item.type);

  return (
    <Card
      sx={{
        maxWidth: 360,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[6],
        position: "relative",
        margin: 1,
        overflow: "visible",
      }}
      variant="outlined"
      data-testid="asset-hover-card"
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ display: "flex", gap: 1, minWidth: 0 }}>
            {assetIcon && (
              <SvgIcon
                sx={{
                  fontSize: 24,
                  color: theme.palette.brand2Text,
                  mt: 0.25,
                  mr: 0.5,
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={assetIcon} />
              </SvgIcon>
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                component="div"
                sx={{ fontWeight: 600, lineHeight: 1.3, color: "text.primary" }}
              >
                {item.title}
              </Typography>
              {hasBreadcrumb && (
                <Box
                  sx={{
                    mt: 0.25,
                    "& .MuiBreadcrumbs-li": { lineHeight: 1.2 },
                    "& [data-testid='breadcrumb']": { lineHeight: 1.2 },
                  }}
                >
                  <AssetBreadcrumbs
                    breadcrumbData={
                      item.breadcrumbs ? JSON.parse(item.breadcrumbs) : []
                    }
                    rootId={currentRootId}
                    version="mini"
                  />
                </Box>
              )}
            </Box>
          </Box>
          {canBookmark && (
            <IconBox className="iconBox">
              <Bookmark
                enabled={item.isBookmark}
                itemType="assets"
                itemId={item.id}
                assetPath={item.path}
                size="large"
              />
            </IconBox>
          )}
        </Box>

        {item.summary && (
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mt: 1,
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {item.summary}
          </Typography>
        )}

        <Box sx={{ mt: 1.25 }}>
          <MediaInfo infoList={createMediaInfo(item, "assets", t)} />
        </Box>
      </CardContent>
    </Card>
  );
};

AssetHoverCard.propTypes = {
  item: PropTypes.shape({
    title: PropTypes.string.isRequired,
    summary: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.string.isRequired,
    type: PropTypes.number.isRequired,
    icon: PropTypes.string,
    isBookmark: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    path: PropTypes.string.isRequired,
  }).isRequired,
  hasBreadcrumb: PropTypes.bool,
};
